import React, { useState, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  useParams,
  useSearchParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { toast } from "sonner";
import paths from "../../routes";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { TabNavigation } from "../../components/ui/tab-navigation";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
} from "../../components/ui/pagination";
import TiptapEditor from "../../components/ui/TiptapEditor";
import {
  Upload,
  FileText,
  Code,
  Plus,
  Trash2,
  Database,
  Search,
  Check,
} from "lucide-react";
// import { useRemoveDatasetMutation } from "../../api/createProjectApi"; // Not used anymore with PATCH API
import { useEnhancedDatasetsQuery } from "../../api/createDatasetApi";
import {
  getProjectById,
  getTemplateById,
} from "../../api/projectManagementApi";
import { StepProps } from "./types";
import AddNewDatasetPage from "../AddNewDatasetPage";

// Helper function to generate unique IDs
const makeId = () =>
  typeof crypto !== "undefined" &&
  typeof (crypto as any).randomUUID === "function"
    ? (crypto as any).randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export const ProjectAssetsStep = React.memo<StepProps>(
  ({ projectData, setProjectData, temporaryProjectId }) => {
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const [showAddDatasetModal, setShowAddDatasetModal] = useState(false);
    const [activeTab, setActiveTab] = useState("select");
    const [searchQuery, setSearchQuery] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [filesToDelete, setFilesToDelete] = useState<string[]>(
      projectData.filesToDelete || [],
    ); // Track files marked for deletion
    const itemsPerPage = 6; // 2 rows × 3 columns
    const navigate = useNavigate();

    const { projectId } = useParams();
    // const { mutate: removeDatasetMutate } = useRemoveDatasetMutation(); // Not used anymore with PATCH API
    const mode = searchParams.get("mode");
    const action = searchParams.get("action");
    const resolvedId = projectId || temporaryProjectId;

    // Track if we have already initialized from server data to prevent overwriting local changes
    const [isInitialized, setIsInitialized] = useState(false);

    // Always fetch available datasets for selection with server-side pagination
    // Exclude datasets with visibility "PROJECT" (those are auto-created from uploads)
    const {
      data: datasetsResponse,
      isLoading: datasetsLoading,
      refetch: refetchDatasets,
    } = useEnhancedDatasetsQuery(
      {
        page: currentPage,
        limit: itemsPerPage,
        ...(searchQuery && { search: searchQuery }), // Only pass search if it has a value
      },
      { enabled: true },
    );

    const { data, isLoading } = useQuery({
      queryKey: ["project", resolvedId],
      queryFn: () => getProjectById(resolvedId || ""),
      enabled: !!resolvedId && mode !== "template" && !isInitialized,
    });

    const { data: templateData, isLoading: templateLoading } = useQuery({
      queryKey: ["template", resolvedId],
      queryFn: () => getTemplateById(resolvedId || ""),
      enabled:
        !!resolvedId &&
        mode === "template" &&
        action === "edit" &&
        !isInitialized,
    });

    // Handle template data
    useEffect(() => {
      if (
        templateData &&
        action === "edit" &&
        mode === "template" &&
        !isInitialized
      ) {
        if ((templateData as any)?.project) {
          setProjectData((prev) => ({
            ...prev,
            ...((templateData as any).project || {}),
            title: (templateData as any).project?.template_name || prev.title,
            description:
              (templateData as any).project?.template_description ||
              prev.description,
            estimatedHours:
              (templateData as any).project?.estimated_hours ||
              prev.estimatedHours,
          }));
          setIsInitialized(true);
        }
      }
    }, [templateData, action, mode, setProjectData, isInitialized]);

    // Handle project data
    useEffect(() => {
      if (data && !isInitialized) {
        setProjectData((prev: any) => ({ ...prev, ...data }));
        setIsInitialized(true);
      }
    }, [data, setProjectData, isInitialized]);

    // Pre-fill project data and linked datasets in ALL edit/template modes
    useEffect(() => {
      if ((!data && !templateData) || isInitialized) return;

      // Helper to process datasets from either project or template data
      const processDatasets = (sourceData: any) => {
        if (!sourceData?.datasets || sourceData.datasets.length === 0) return;

        const linkedDatasetIds = sourceData.datasets.map((ds: any) => ds.id);

        setProjectData((prev: any) => {
          const updatedData = { ...prev, ...sourceData };

          updatedData.selectedDatasetIds = linkedDatasetIds;
          updatedData.originalLinkedDatasetIds = linkedDatasetIds;
          updatedData.selectedDatasetId = linkedDatasetIds[0] || "";
          updatedData.originalLinkedDatasetId = linkedDatasetIds[0] || "";

          if (process.env.NODE_ENV === "development") {
            console.log("[Dataset Pre-fill]", {
              source: templateData ? "template" : "project",
              datasets: sourceData.datasets.map((ds: any) => ds.title),
              selectedIds: linkedDatasetIds,
            });
          }

          return updatedData;
        });
        setIsInitialized(true);
      };

      if (data && mode !== "template") {
        processDatasets(data);
      }

      if (templateData && mode === "template" && action === "edit") {
        if ((templateData as any)?.project?.datasets) {
          processDatasets((templateData as any).project); // Template returns {template, project}
        }
      }
    }, [data, templateData, mode, action, setProjectData, isInitialized]);

    // Drag and drop handler with react-dropzone
    const onDrop = useCallback(
      (acceptedFiles: File[]) => {
        const newDatasets = acceptedFiles.map((file) => {
          const kb = Math.round(file.size / 1024);
          const sizeStr =
            kb < 1024 ? `${kb} KB` : `${(kb / 1024).toFixed(2)} MB`;

          return {
            id: makeId(),
            name: file.name,
            file: file,
            size: sizeStr,
          };
        });

        setProjectData((prev) => ({
          ...prev,
          datasets: [...(prev.datasets ?? []), ...newDatasets],
        }));

        if (acceptedFiles.length === 1 && acceptedFiles[0]) {
          toast.success(`Added ${acceptedFiles[0].name}`);
        } else {
          toast.success(`Added ${acceptedFiles.length} files`);
        }
      },
      [setProjectData],
    );

    const { getRootProps, getInputProps, isDragActive, isDragReject } =
      useDropzone({
        onDrop,
        accept: {
          "text/csv": [".csv"],
          "application/json": [".json"],
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
            ".xlsx",
          ],
          "application/octet-stream": [".parquet"],
        },
        multiple: true,
      });

    // Handler for removing newly added datasets
    const handleRemoveNewDataset = (datasetId: string, datasetName: string) => {
      setProjectData((prev) => ({
        ...prev,
        datasets:
          prev.datasets?.filter((dataset) => dataset.id !== datasetId) ?? [],
      }));

      toast.success(`Removed ${datasetName}`);
    };

    // Handler for marking existing uploaded files (PROJECT visibility datasets) for deletion
    const handleMarkFileForDeletion = (
      s3Key: string,
      datasetId: string,
      fileName: string,
    ) => {
      // Add to local filesToDelete array
      setFilesToDelete((prev) => [...prev, s3Key]);

      // Update projectData to include filesToDelete
      // We don't mark the WHOLE dataset as deleted anymore, as it might contain other files
      setProjectData((prev) => {
        const currentFilesToDelete = prev.filesToDelete || [];
        if (currentFilesToDelete.includes(s3Key)) return prev;

        return {
          ...prev,
          filesToDelete: [...currentFilesToDelete, s3Key],
          // Note: We removed _markedForDeletion on dataset level to avoid hiding other files in same dataset
        };
      });

      toast.success(`Marked ${fileName} for deletion`);
    };

    // Sync checkpoints with project ID
    useEffect(() => {
      if (!projectData.projectId) return;
      setProjectData((prev) => {
        const checkpoints = (prev.checkpoints ?? [])?.map((cp) => ({
          ...cp,
          project_id: projectData.id,
        }));
        return { ...prev, checkpoints };
      });
    }, [projectData.projectId, projectData.id, setProjectData]);

    return (
      <div className="space-y-6">
        {isLoading || templateLoading ? (
          <Card className="h-[50vh]">
            <p className="flex justify-center items-center h-full">
              Fetching Project Details...
            </p>
          </Card>
        ) : (
          <>
            {/* Add Dataset Card with Tabs */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center space-x-2">
                      <Database className="h-5 w-5 text-bits-blue" />
                      <CardTitle>Add Dataset</CardTitle>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      Select dataset or upload files directly
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowAddDatasetModal(true)}
                    className="text-bits-blue hover:bg-blue-50"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Create new dataset
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Tab Navigation */}
                <TabNavigation
                  tabs={[
                    {
                      key: "select",
                      label: "Select Dataset",
                      icon: <Database className="h-4 w-4" />,
                    },
                    {
                      key: "upload",
                      label: "Upload File",
                      icon: <Upload className="h-4 w-4" />,
                    },
                  ]}
                  activeTab={activeTab}
                  onTabChange={setActiveTab}
                  className="mb-6"
                />

                {/* Select Dataset Tab */}
                {activeTab === "select" && (
                  <div className="space-y-4">
                    {/* Search Bar */}
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="Search file, tag..."
                        value={searchQuery}
                        onChange={(e) => {
                          setSearchQuery(e.target.value);
                          setCurrentPage(1); // Reset to first page on search
                        }}
                        className="pl-10"
                      />
                    </div>

                    {/* Dataset Cards Grid */}
                    {datasetsLoading ? (
                      <div className="text-center py-8 text-gray-500">
                        Loading datasets...
                      </div>
                    ) : datasetsResponse?.datasets &&
                      datasetsResponse.datasets.length > 0 ? (
                      <>
                        {(() => {
                          const currentDatasets =
                            datasetsResponse.datasets.filter(
                              (ds) => ds.visibility !== "PROJECT",
                            );
                          const totalPages =
                            datasetsResponse.pagination?.total_pages || 1;
                          const totalItems =
                            datasetsResponse.pagination?.total ||
                            datasetsResponse.datasets.length;

                          const getVisiblePages = () => {
                            const pages: number[] = [];
                            const maxVisible = 5;

                            if (totalPages <= maxVisible) {
                              for (let i = 1; i <= totalPages; i++) {
                                pages.push(i);
                              }
                            } else {
                              if (currentPage <= 3) {
                                for (let i = 1; i <= 3; i++) {
                                  pages.push(i);
                                }
                              } else if (currentPage >= totalPages - 2) {
                                for (
                                  let i = totalPages - 2;
                                  i <= totalPages;
                                  i++
                                ) {
                                  pages.push(i);
                                }
                              } else {
                                pages.push(
                                  currentPage - 1,
                                  currentPage,
                                  currentPage + 1,
                                );
                              }
                            }
                            return pages;
                          };

                          const visiblePages = getVisiblePages();

                          return (
                            <>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {currentDatasets.map((dataset) => {
                                  const isSelected =
                                    projectData.selectedDatasetIds?.includes(
                                      dataset.id,
                                    );

                                  return (
                                    <div
                                      key={dataset.id}
                                      onClick={() => {
                                        const currentIds =
                                          projectData.selectedDatasetIds || [];
                                        const newIds = isSelected
                                          ? currentIds.filter(
                                              (id) => id !== dataset.id,
                                            )
                                          : [...currentIds, dataset.id];
                                        setProjectData((prev) => ({
                                          ...prev,
                                          selectedDatasetIds: newIds,
                                          selectedDatasetId: newIds[0] || "",
                                        }));
                                      }}
                                      className={`relative cursor-pointer rounded-lg border-2 transition-all hover:shadow-md ${
                                        isSelected
                                          ? "border-bits-blue bg-blue-50"
                                          : "border-gray-200 bg-white hover:border-gray-300"
                                      }`}
                                    >
                                      <div
                                        className={`absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 rounded border-2 flex items-center justify-center transition-colors z-10 ${
                                          isSelected
                                            ? "bg-bits-blue border-bits-blue"
                                            : "bg-white border-gray-300"
                                        }`}
                                      >
                                        {isSelected && (
                                          <Check className="h-3 w-3 text-white" />
                                        )}
                                      </div>

                                      <div className="flex items-start p-4 pr-12">
                                        <div className="w-32 h-28 rounded overflow-hidden shrink-0">
                                          {dataset.coverPhotoUrl ? (
                                            <img
                                              src={dataset.coverPhotoUrl}
                                              alt={dataset.title}
                                              className="w-full h-full object-cover"
                                              crossOrigin="anonymous"
                                              loading="lazy"
                                              onError={(e) => {
                                                (
                                                  e.target as HTMLImageElement
                                                ).style.display = "none";
                                              }}
                                            />
                                          ) : (
                                            <div className="w-full h-full bg-linear-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                                              <Database className="h-10 w-10 text-gray-400 opacity-50" />
                                            </div>
                                          )}
                                        </div>

                                        <div className="ml-4 flex-1 min-w-0">
                                          <h3
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              navigate(
                                                paths.toViewDataset(dataset.id),
                                                {
                                                  state: {
                                                    returnTo:
                                                      "project-creation",
                                                    returnUrl: (() => {
                                                      const params =
                                                        new URLSearchParams(
                                                          location.search,
                                                        );
                                                      params.set("step", "2");
                                                      return `${location.pathname}?${params.toString()}`;
                                                    })(),
                                                  },
                                                },
                                              );
                                            }}
                                            className={`font-semibold text-sm mb-1 line-clamp-2 hover:underline cursor-pointer ${
                                              isSelected
                                                ? "text-bits-blue"
                                                : "text-gray-900"
                                            }`}
                                          >
                                            {dataset.title}
                                          </h3>

                                          <p className="text-xs text-gray-600 mb-2">
                                            by{" "}
                                            <span className="font-medium">
                                              {dataset?.uploader?.name || "Unknown"}
                                            </span>{" "}
                                            • Updated on{" "}
                                            {dataset?.updatedAt
                                              ? new Date(
                                                  dataset?.updatedAt,
                                                ).toLocaleDateString()
                                              : "N/A"}
                                          </p>

                                          {dataset.description && (
                                            <p className="text-xs text-gray-500 line-clamp-2 mb-2">
                                              {dataset.description}
                                            </p>
                                          )}

                                          {dataset.tags &&
                                            dataset.tags.length > 0 && (
                                              <div className="flex flex-wrap gap-1">
                                                {dataset.tags
                                                  .slice(0, 3)
                                                  .map((tag, idx) => (
                                                    <span
                                                      key={idx}
                                                      className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600"
                                                    >
                                                      {tag}
                                                    </span>
                                                  ))}
                                              </div>
                                            )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>

                              {totalPages > 1 && (
                                <Pagination className="mt-6">
                                  <PaginationContent>
                                    <PaginationItem>
                                      <PaginationPrevious
                                        href="#"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          if (currentPage > 1)
                                            setCurrentPage(currentPage - 1);
                                        }}
                                        aria-disabled={currentPage === 1}
                                        className={
                                          currentPage === 1
                                            ? "pointer-events-none opacity-50"
                                            : ""
                                        }
                                      />
                                    </PaginationItem>
                                    {visiblePages.map((pageNum) => (
                                      <PaginationItem key={pageNum}>
                                        <PaginationLink
                                          href="#"
                                          isActive={pageNum === currentPage}
                                          onClick={(e) => {
                                            e.preventDefault();
                                            setCurrentPage(pageNum);
                                          }}
                                        >
                                          {pageNum}
                                        </PaginationLink>
                                      </PaginationItem>
                                    ))}
                                    <PaginationItem>
                                      <PaginationNext
                                        href="#"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          if (currentPage < totalPages)
                                            setCurrentPage(currentPage + 1);
                                        }}
                                        aria-disabled={
                                          currentPage === totalPages
                                        }
                                        className={
                                          currentPage === totalPages
                                            ? "pointer-events-none opacity-50"
                                            : ""
                                        }
                                      />
                                    </PaginationItem>
                                  </PaginationContent>
                                </Pagination>
                              )}
                            </>
                          );
                        })()}
                      </>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <Database className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                        <p className="text-sm">No datasets available</p>
                      </div>
                    )}

                    {projectData.selectedDatasetIds &&
                      projectData.selectedDatasetIds.length > 0 && (
                        <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                          <div className="flex items-center gap-2">
                            <Check className="h-4 w-4 text-bits-blue" />
                            <span className="text-sm font-medium text-bits-blue">
                              {projectData.selectedDatasetIds.length} dataset(s)
                              selected
                            </span>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              setProjectData((prev) => ({
                                ...prev,
                                selectedDatasetIds: [],
                                selectedDatasetId: "",
                              }))
                            }
                            className="text-bits-blue hover:text-blue-700 hover:bg-blue-100"
                          >
                            Clear all
                          </Button>
                        </div>
                      )}
                  </div>
                )}

                {/* Upload File Tab */}
                {activeTab === "upload" && (
                  <div className="space-y-4">
                    <div
                      {...getRootProps()}
                      className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer
                        ${isDragActive && !isDragReject ? "border-bits-blue bg-blue-50" : ""}
                        ${isDragReject ? "border-red-500 bg-red-50" : ""}
                        ${!isDragActive ? "border-gray-300 bg-bits-grey-100 hover:border-bits-blue hover:bg-blue-50" : ""}
                      `}
                    >
                      <input {...getInputProps()} />
                      <Upload
                        className={`h-12 w-12 mx-auto mb-4 ${
                          isDragActive ? "text-bits-blue" : "text-gray-400"
                        }`}
                      />
                      <p className="text-sm text-gray-600 mb-2">
                        <span className="font-medium text-bits-blue">
                          Click to upload
                        </span>{" "}
                        or drag and drop
                      </p>
                      <p className="text-xs text-gray-500">
                        CSV, JSON, XLSX, or Parquet files
                      </p>
                    </div>

                    <div className="space-y-2">
                      {(() => {
                        const newlyUploadedFiles =
                          projectData?.datasets?.filter((ds: any) => ds.file) ||
                          [];
                        const projectDatasets =
                          projectData?.datasets?.filter(
                            (ds: any) => ds.visibility === "PROJECT"
                          ) || [];

                        // Count total files
                        const newlyUploadedCount = newlyUploadedFiles.length;
                        const projectFilesCount = projectDatasets.reduce(
                          (sum: number, ds: any) => sum + (ds.s3_keys?.length || 0),
                          0
                        );
                        const totalFilesCount = newlyUploadedCount + projectFilesCount;

                        const allUploadedFiles = [
                          ...newlyUploadedFiles,
                          ...projectDatasets,
                        ];

                        return totalFilesCount > 0 ? (
                          <>
                            <div className="text-sm font-medium text-gray-700 mb-2">
                              Uploaded Files ({totalFilesCount})
                            </div>
                            {allUploadedFiles.map((dataset: any, index) => {
                              const isNewUpload = !!dataset.file;

                              if (!isNewUpload && dataset.s3_keys) {
                                return dataset.s3_keys.map(
                                  (s3File: any, fileIndex: number) => {
                                    const fileName =
                                      s3File.originalName ||
                                      s3File.key?.split("/").pop() ||
                                      "Unknown file";
                                    const isMarkedForDeletion =
                                      filesToDelete.includes(s3File.key);

                                    return (
                                      <div
                                        key={`dataset-uploaded-${dataset.id}-file-${fileIndex}`}
                                        className={`flex items-center justify-between p-3 rounded-lg ${
                                          isMarkedForDeletion
                                            ? "bg-red-50 opacity-60"
                                            : "bg-blue-50"
                                        }`}
                                      >
                                        <div className="flex items-center space-x-3">
                                          <FileText className="h-4 w-4 text-blue-600" />
                                          <div>
                                            <p
                                              className={`text-sm font-medium ${isMarkedForDeletion ? "line-through text-gray-400" : ""}`}
                                            >
                                              {fileName}
                                            </p>
                                            <p className="text-xs text-gray-400 mt-1">
                                              {isMarkedForDeletion
                                                ? "Marked for deletion"
                                                : "Uploaded to project"}
                                            </p>
                                          </div>
                                        </div>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() =>
                                            handleMarkFileForDeletion(
                                              s3File.key,
                                              dataset.id,
                                              fileName,
                                            )
                                          }
                                          disabled={isMarkedForDeletion}
                                          className={`${
                                            isMarkedForDeletion
                                              ? "cursor-not-allowed opacity-50"
                                              : "hover:bg-red-50 hover:text-red-600"
                                          }`}
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    );
                                  },
                                );
                              }

                              return (
                                <div
                                  key={`dataset-uploaded-${dataset.id || index}`}
                                  className="flex items-center justify-between p-3 rounded-lg bg-green-50"
                                >
                                  <div className="flex items-center space-x-3">
                                    <FileText className="h-4 w-4 text-blue-600" />
                                    <div>
                                      <p className="text-sm font-medium">
                                        {dataset.name}
                                      </p>
                                      <p className="text-xs text-gray-400 mt-1">
                                        Ready to upload
                                      </p>
                                    </div>
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() =>
                                      handleRemoveNewDataset(
                                        dataset.id,
                                        dataset.name,
                                      )
                                    }
                                    className="hover:bg-red-50 hover:text-red-600"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              );
                            })}
                          </>
                        ) : (
                          <div className="text-center py-8 text-gray-500">
                            <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                            <p className="text-sm">No files uploaded yet</p>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <AddNewDatasetPage
              isOpen={showAddDatasetModal}
              onClose={() => setShowAddDatasetModal(false)}
              onSuccess={async (newDataset) => {
                await refetchDatasets();
                if (newDataset && newDataset.id) {
                  setProjectData((prev) => {
                    const updatedIds = [
                      ...(prev.selectedDatasetIds || []),
                      newDataset.id,
                    ];
                    return {
                      ...prev,
                      selectedDatasetIds: updatedIds,
                      selectedDatasetId: updatedIds[0],
                    };
                  });
                }
                setShowAddDatasetModal(false);
              }}
            />

            <Card>
              <CardHeader>
                <div className="flex items-center space-x-2">
                  <Code className="h-5 w-5 text-bits-blue" />
                  <CardTitle>Project Instructions & Notebook</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 border-b">
                    <h4 className="font-medium">Readme.md</h4>
                  </div>
                  <TiptapEditor
                    id="project_instructions"
                    placeholder="# Project Instructions"
                    className="font-mono text-sm border-0 bg-bits-grey-100 text-bits-grey-600"
                    value={projectData.instructions}
                    onChange={(content) =>
                      setProjectData((prev) => ({
                        ...prev,
                        instructions: content,
                      }))
                    }
                  />
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    );
  },
);

ProjectAssetsStep.displayName = "ProjectAssetsStep";
